import { useEffect, useState } from "react";
import ArrowCircleRightIcon from "../../assets/icons/arrow-circle-right.svg?react";
import WebsiteIcon from "../../assets/icons/website.svg?react";
import CustomIcon from "../../components/CustomIcon";
import Loader from "../../components/Loader";
import { displayToast } from "../../components/app/AppToast";
import ClampLines from "../../components/app/ClampLines";
import ExpandableText from "../../components/app/ExpandableText";
import PlaylistCarousel from "../../components/app/PlaylistCarousel";
import ContentHeader from "../../components/app/headers/ContentHeader";
import { NavShare } from "../../components/app/headers/layout";
import i18n from "../../config/i18n";
import {
  useCurrentUser,
  type CurrentUser,
} from "../../domains/user/hooks/useCurrentUser";
import { renderDateRelative } from "../../domains/app/utils/renderDate";
import { shareUrl } from "../../domains/app/utils/shareUrl";
import { apiClient, useApiQuery, type Api } from "../../services/api";
import { useGetCompanyPlaylistsQuery } from "../../domains/playlist/hooks/useGetCompanyPlaylistsQuery";
import { AnalyticsItemList } from "../../tools/analytics/analytics.types";
import {
  gaEventCompanyRedirect,
  gaEventCompanyShare,
} from "../../tools/analytics/domains/company.analytics";
import ScrollableLayout from "../../components/layout/ScrollableLayout";
import { useTranslation } from "react-i18next";

type Company = Api["get"]["/webapp/public/companies/:id"]["output"]["body"];

export default function CompanyPage({ id }: { id: string }) {
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const companyQuery = useApiQuery("/webapp/public/companies/:id", {
    param: { id },
  });
  useEffect(() => {
    if (currentUser !== null) {
      void apiClient.post("/webapp/private/companies/:id/view", {
        param: { id },
      });
    }
  }, [id, currentUser]);

  const [isTextExpanded, setTextExpanded] = useState(false);
  const cache = useGetCompanyPlaylistsQuery({
    companyId: id,
  });
  const displayedPlaylists = cache.playlists.slice(0, 5);

  async function handleShare(company: Company, currentUser: CurrentUser) {
    try {
      const text =
        i18n.resolvedLanguage === "fr"
          ? `Découvrez les dernières nouvelles de ${company.name} sur Juisci !`
          : `Discover the latest news of ${company.name} on Juisci now!`;
      const url = `${globalThis.location.origin}/company/${company.id}?shared=${currentUser.id}`;
      // Not awaited: `shareUrl` must start inside the click (see there).
      const link = apiClient
        .post("/webapp/private/companies/:id/share", {
          param: { id: company.id },
        })
        .then(() => url);
      const outcome = await shareUrl(document.title, text, link);

      gaEventCompanyShare({
        company,
        listName: AnalyticsItemList.ORGANISATION_PAGE,
      });
      if (outcome === "sent")
        displayToast(t("Company profile shared"), "success");
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  function handleClickWebsite(company: Company) {
    gaEventCompanyRedirect({
      company,
      listName: AnalyticsItemList.ORGANISATION_PAGE,
    });
  }

  if (companyQuery.state === "pending") {
    return (
      <ScrollableLayout
        headerClassName="bg-white/90 backdrop-blur-md"
        header={<ContentHeader transparent />}
      >
        <Loader expanded />
      </ScrollableLayout>
    );
  }

  if (companyQuery.state === "error") {
    return (
      <ScrollableLayout
        headerClassName="bg-white/90 backdrop-blur-md"
        header={<ContentHeader transparent />}
      >
        <div style={{ padding: "24px", textAlign: "center" }}>
          <p>
            {companyQuery.status === null ? "" : `${companyQuery.status} — `}
            {companyQuery.message}
          </p>
        </div>
      </ScrollableLayout>
    );
  }

  const company = companyQuery.body;

  return (
    <ScrollableLayout
      headerClassName="bg-white/90 backdrop-blur-md"
      header={
        <ContentHeader
          transparent
          title={company.name}
          actions={
            currentUser === null ? undefined : (
              <NavShare onClick={() => handleShare(company, currentUser)} />
            )
          }
        />
      }
    >
      <div className="bg-white p-[24px_22px] flex flex-col gap-4">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "16px",
          }}
        >
          <img
            src={company.logo.url}
            alt={company.name + " logo"}
            className="max-w-[80px] h-[40px] object-contain"
          />
          <div style={{ flex: 1 }}>
            <h5 className="text-primary-black font-dm-sans text-sm font-semibold leading-[140%] m-0">
              {company.name}
            </h5>
            <h6 className="text-primary-black font-dm-sans text-xs font-light leading-[140%] m-0">
              {company.type}
            </h6>
          </div>
          {company.website !== null && (
            <a
              href={company.website}
              target="_blank"
              onClick={() => {
                handleClickWebsite(company);
              }}
              rel="noreferrer"
            >
              <WebsiteIcon />
            </a>
          )}
        </div>
        <ExpandableText
          isExpanded={isTextExpanded}
          setExpand={setTextExpanded}
          lines={10}
        >
          <span className="text-primary-black font-dm-sans text-sm font-normal leading-[160%] m-0">
            {company.description}
          </span>
        </ExpandableText>
        {company.video_link !== null && company.video_link !== "" && (
          <VideoCard
            link={company.video_link}
            thumbnail={`https://i3.ytimg.com/vi/${
              company.video_link.split("v=").at(1) ?? ""
            }/mqdefault.jpg`}
          />
        )}
      </div>

      {/* NOTE: Quick fix to hide this block if list comes empty (rooms), remove condition when fixed. */}
      {(cache.isLoading || displayedPlaylists.length > 0) && (
        <PlaylistCarousel
          title={t("{{name}} collections", { name: company.name })}
          linkTo={`/company/${company.id}/playlists`}
          playlists={displayedPlaylists}
          backgroundColor="var(--color-secondary-soft-yellow-2)"
        />
      )}

      <div className="flex-1 bg-[linear-gradient(180deg,#ff699c_0%,#fe5763_25%,#ff8a00_50%,#fdb955_75%,#ffc408_100%)] p-[24px_8px] flex flex-col gap-[10px] text-white">
        {company.infos.length > 0 ? (
          company.infos.map((info, index) => {
            return (
              <EventItem
                key={`${index}--event-item`}
                info={info}
                company={company}
              />
            );
          })
        ) : (
          <p>{t("No events for the moment")}</p>
        )}
      </div>
    </ScrollableLayout>
  );
}

interface EventItemProps {
  info: Company["infos"][number];
  company: Company;
}

const EventItem: React.FC<EventItemProps> = ({ info, company }) => {
  return (
    <a href={info.link} target="_blank" rel="noreferrer">
      <div className="bg-white p-4 shadow-card rounded-2xl text-primary-black flex flex-col gap-4 [&_p]:text-primary-black [&_p]:font-dm-sans [&_p]:text-base [&_p]:font-bold [&_p]:leading-[130%] [&_p]:tracking-[-0.16px] [&_p]:m-0 [&_p.date]:font-dm-sans [&_p.date]:text-[11px] [&_p.link]:font-bold [&_p.link]:text-sm [&_p.link]:leading-[21px] [&_p.link]:font-dm-sans [&_div.tag]:bg-secondary-soft-pink-1 [&_div.tag]:text-secondary-raspberry-1 [&_div.tag]:rounded-lg [&_div.tag]:text-[11px] [&_div.tag]:font-normal [&_div.tag]:font-dm-sans [&_div.tag]:table [&_div.tag]:p-[2px_8px] [&_div.tag]:box-border [&_div.tag.date]:bg-primary-black [&_div.tag.date]:text-white [&_div.tag.date]:font-medium [&_div.publication_date]:text-primary-black [&_div.publication_date]:font-bold [&_div.publication_date]:text-[10px] [&_div.publication_date]:leading-[120%] [&_div.publication_date]:text-end [&_div.publication_date]:tracking-[0.02em] [&_div.publication_date]:font-dm-sans [&_div.publication_date]:whitespace-nowrap [&_img]:w-[50px] [&_img]:h-[40px] [&_img]:object-contain [&_h5]:text-primary-black [&_h5]:font-dm-sans [&_h5]:text-sm [&_h5]:font-semibold [&_h5]:leading-[140%] [&_h5]:m-0 [&_h6]:text-primary-black [&_h6]:font-dm-sans [&_h6]:text-xs [&_h6]:font-light [&_h6]:leading-[140%] [&_h6]:m-0">
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "15px",
          }}
        >
          <img src={company.logo.url} alt={company.name + " logo"} />
          <div style={{ width: "100%" }}>
            <h5>{company.name}</h5>
            <h6>{company.type}</h6>
          </div>
          {info.creation_date !== null && (
            <div style={{ alignSelf: "flex-start" }}>
              <div className="publication_date">
                {renderDateRelative(new Date(info.creation_date))}
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}
        >
          <div className="tag">{info.type}</div>
        </div>

        <ClampLines lines={8}>
          <p>{info.content}</p>
        </ClampLines>

        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <ArrowCircleRightIcon />
          <p className="link">{info.cta}</p>
        </div>
      </div>
    </a>
  );
};

function VideoCard({ link, thumbnail }: { link: string; thumbnail: string }) {
  const { t } = useTranslation();

  return (
    <a href={link} target="_blank" rel="noreferrer">
      <div
        className="relative h-[200px] w-full rounded-2xl p-[15px] box-border bg-cover [&_.play-icon]:absolute [&_.play-icon]:top-1/2 [&_.play-icon]:left-1/2 [&_.play-icon]:-translate-1/2  [&_.tag]:bg-primary-black [&_.tag]:rounded-lg [&_.tag]:text-white [&_.tag]:text-xs [&_.tag]:font-normal [&_.tag]:font-dm-sans [&_.tag]:table [&_.tag]:p-[5px_10px]"
        style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.1) 50%, rgba(0,0,0,0.4) 100%), url(${thumbnail})`,
        }}
      >
        <div className="tag">{t("Video")}</div>
        <div className="play-icon">
          <CustomIcon iconName="play_circle" />
        </div>
      </div>
    </a>
  );
}
